import * as Sentry from '@sentry/react';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { isCancel } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useAuthenticated } from '../utils/auth/auth.js';

export type DataResult<T> = { data: T; error: undefined; loading: false };
export type ErrorResult = {
  data: undefined;
  error: AxiosError;
  loading: false;
};
export type LoadingResult = {
  data: undefined;
  error: undefined;
  loading: true;
};

export type UseAxiosResult<T> = DataResult<T> | ErrorResult | LoadingResult;
export type UseAxiosResponse<T> = [
  result: UseAxiosResult<T>,
  refresh: () => void,
  fetchNow: (controller?: AbortController) => Promise<T>,
];

interface UseAxiosOptions {
  condition?: boolean;
  onLoaded?: () => void;
}

export const useAxios = <T>(
  url: string,
  axiosOptions?: AxiosRequestConfig,
  options: UseAxiosOptions = {},
): UseAxiosResponse<T> => {
  const auth = useAuthenticated();
  const [data, setData] = useState<T>();
  const [error, setError] = useState<AxiosError | undefined>();
  const [loading, setLoading] = useState(false);
  const [instance, setInstance] = useState(Date.now());
  const condition = options.condition;

  const refresh = useCallback(() => setInstance(Date.now()), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: works, don't want to mess with it.
  const fetchNow = useCallback(
    async (controller?: AbortController) => {
      const response = await auth.axios.request<T>({
        method: 'GET',
        url,
        ...axiosOptions,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Expires: '0',
          Pragma: 'no-cache',
          ...axiosOptions?.headers,
        },
        signal: controller?.signal,
      });
      return response.data;
    },
    [url, axiosOptions],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: works, don't want to mess with it.
  useEffect(() => {
    if (condition === false) return;

    const controller = new AbortController();
    setLoading(true);
    fetchNow(controller)
      .then((response) => {
        setData(response);
        setLoading(false);
        options?.onLoaded?.();
        return null;
      })
      .catch((error: unknown) => {
        if (isCancel(error)) {
          Sentry.addBreadcrumb({
            data: {
              url,
            },
            level: 'info',
            message: 'Request cancelled',
          });
          return null;
        }

        const { toJSON } = error as { toJSON: () => string };
        console.error(
          'Axios Error',
          toJSON ? toJSON.apply(error) : JSON.stringify(error),
          error,
        );
        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(
            `Axios (non-)Error: ${JSON.stringify(error)}`,
            'error',
          );
        }
        setError(error as AxiosError);
        setLoading(false);
        return null;
      });
    return () => controller.abort();
  }, [condition, fetchNow, instance]);

  if (loading) {
    return [{ data: undefined, error: undefined, loading }, refresh, fetchNow];
  }
  if (error) {
    return [{ data: undefined, error, loading: false }, refresh, fetchNow];
  }
  if (data !== undefined) {
    return [{ data, error: undefined, loading: false }, refresh, fetchNow];
  }
  return [
    { data: undefined, error: undefined, loading: true },
    refresh,
    fetchNow,
  ];
};
